import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

function fmt(v) { return Number(v||0).toLocaleString('fr-FR') + ' F'; }
function fmtDate(d) { if (!d) return '—'; return new Date(d).toLocaleDateString('fr-FR', { day:'2-digit', month:'short', year:'numeric' }); }
function today() { return new Date().toISOString().split('T')[0]; }

const STATUT_CONFIG = {
  BROUILLON: { label:'Brouillon', bg:'#F3F4F6', color:'#6B7280', dot:'#9CA3AF' },
  SOUMIS:    { label:'Soumis',    bg:'#EFF6FF', color:'#3B82F6', dot:'#3B82F6' },
  VALIDE:    { label:'Validé',    bg:'#E8F5E9', color:'#2E7D32', dot:'#2E7D32' },
  REJETE:    { label:'Rejeté',    bg:'#FFEBEE', color:'#C62828', dot:'#C62828' },
};

function BadgeStatut({ statut }) {
  const c = STATUT_CONFIG[statut] || STATUT_CONFIG.BROUILLON;
  return (
    <span style={{ display:'inline-flex', alignItems:'center', gap:5, background:c.bg, color:c.color, padding:'3px 10px', borderRadius:20, fontSize:11, fontWeight:600 }}>
      <span style={{ width:6, height:6, borderRadius:'50%', background:c.dot, display:'inline-block' }}/>
      {c.label}
    </span>
  );
}

const PAGE_SIZE = 15;

const C = {
  primary: '#2E7D32', primaryDark: '#1B5E20',
  border: '#E0E0E0', light: '#F4F6F9',
  green100: '#E8F5E9', textDark: '#212121', textMid: '#616161', textLight: '#9E9E9E',
};

const S = {
  page: { minHeight: '100vh', background: C.light },
  topbar: { height: 52, background: '#fff', borderBottom: `1px solid ${C.border}`,
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '0 24px', position: 'sticky', top: 0, zIndex: 100, flexShrink: 0 },
  breadcrumb: { fontSize: 13, color: C.textLight, display: 'flex', alignItems: 'center', gap: 6 },
  breadcrumbActive: { color: C.textDark, fontWeight: 600 },
  container: { maxWidth: 1100, margin: '20px auto', padding: '0 20px', paddingBottom: 40 },
  carte: { background: '#fff', borderRadius: 12, border: `0.5px solid ${C.border}`,
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)', marginBottom: 16, overflow: 'hidden' },
  carteBody: { padding: '14px 16px' },
  secHeader: (color) => ({ background: color || C.primary, color: '#fff', padding: '10px 16px', fontSize: 13, fontWeight: 700 }),
  label: { display: 'block', fontSize: 12, color: C.textMid, marginBottom: 4, fontWeight: 600 },
  input: { width: '100%', padding: '8px 10px', border: `1px solid ${C.border}`,
    borderRadius: 7, fontSize: 12, boxSizing: 'border-box', outline: 'none' },
  th: { background: '#F8F9FA', color: C.textMid, padding: '8px 12px', textAlign: 'left',
    fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px',
    borderBottom: `1px solid ${C.border}` },
  td: { padding: '10px 12px', borderBottom: `1px solid #F0F2F4`, fontSize: 12, color: C.textDark },
  tdR: { padding: '10px 12px', borderBottom: `1px solid #F0F2F4`, fontSize: 12, color: C.textDark, textAlign: 'right' },
  btnPrimary: { background: C.primary, color: 'white', border: 'none', padding: '8px 18px',
    borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600 },
  btnSecondaire: { background: '#fff', color: C.textMid, border: `1px solid ${C.border}`, padding: '8px 16px',
    borderRadius: 8, cursor: 'pointer', fontSize: 13 },
  alerte: (t) => ({ padding: '10px 14px', borderRadius: 8, marginBottom: 12, fontSize: 13, fontWeight: 500,
    background: t === 'ok' ? C.green100 : '#FFEBEE',
    color: t === 'ok' ? C.primaryDark : '#C62828',
    border: `1px solid ${t === 'ok' ? '#A5D6A7' : '#EF9A9A'}` }),
};

export default function CaissePage() {
  const navigate = useNavigate();
  const { utilisateur } = useAuth();
  const isAdmin = ['admin','responsable_facturation'].includes(utilisateur?.role);

  const [onglet, setOnglet] = useState('liste');
  const [abattoirs, setAbattoirs] = useState([]);
  const [rapports, setRapports] = useState([]);
  const [stats, setStats] = useState({ total_recettes:0, nb_rapports:0, nb_factures:0 });
  const [chargement, setChargement] = useState(false);
  const [message, setMessage] = useState(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [filtreDate, setFiltreDate] = useState('');
  const [filtreAbattoir, setFiltreAbattoir] = useState('');
  const [filtreStatut, setFiltreStatut] = useState('');
  const [rapportACorrection, setRapportACorrection] = useState(null);
  const [rapportBilletage, setRapportBilletage] = useState(null);
  const [billetage, setBilletage] = useState({ nb_10000:0,nb_5000:0,nb_2000:0,nb_1000:0,nb_500:0,nb_200:0,nb_100:0,nb_50:0,nb_25:0,nb_10:0,nb_5:0 });
  const [billetageExistant, setBilletageExistant] = useState(null);
  const [motifRejet, setMotifRejet] = useState('');
  const [rapportAValider, setRapportAValider] = useState(null);
  const [rapportARejeter, setRapportARejeter] = useState(null);
  const [form, setForm] = useState({
    abattoir_id:'', date_rapport: today(),
    enc_abattoirs:'', enc_stockage_frigo:'', enc_location:'',
    enc_tickets:'', enc_autre:'', enc_autre_libelle:'',
    dec_montant:'', dec_libelle:'', observations:''
  });

  useEffect(() => {
    api.get('/abattoirs').then(r => {
      setAbattoirs(r.data || []);
      if (!isAdmin && r.data.length > 0)
        setForm(f => ({ ...f, abattoir_id: String(utilisateur?.abattoir_id || r.data[0].id) }));
    }).catch(() => {});
  }, []);

  const charger = useCallback(async (p = 1) => {
    setChargement(true);
    try {
      const params = new URLSearchParams({ page: p, limite: PAGE_SIZE });
      if (filtreDate) { params.append('date_debut', filtreDate); params.append('date_fin', filtreDate); }
      if (filtreAbattoir) params.append('abattoir_id', filtreAbattoir);
      if (filtreStatut) params.append('statut', filtreStatut);
      const r = await api.get(`/caisses?${params}`);
      setRapports(r.data.rapports || []);
      setStats(r.data.stats || {});
      setTotal(r.data.pagination?.total || 0);
      setPage(p);
    } catch { setMessage({ type:'err', texte:'Erreur de chargement.' }); }
    finally { setChargement(false); }
  }, [filtreDate, filtreAbattoir, filtreStatut]);

  useEffect(() => { if (onglet === 'liste') charger(1); }, [onglet]);

  const champ = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const totalEnc = () => ['enc_abattoirs','enc_stockage_frigo','enc_location','enc_tickets','enc_autre'].reduce((s,k) => s+(parseFloat(form[k])||0), 0);
  const soldeNet = () => totalEnc() - (parseFloat(form.dec_montant)||0);
  const nbPages = Math.ceil(total / PAGE_SIZE);

  const isCaissierPrincipal = ['admin','responsable_facturation','caissier_principal'].includes(utilisateur?.role);

  const ouvririCorrection = (rapport) => {
    setRapportACorrection(rapport);
    setForm({
      abattoir_id: String(rapport.abattoir_id),
      date_rapport: rapport.date_rapport?.split('T')[0] || today(),
      enc_abattoirs: rapport.enc_abattoirs || '',
      enc_stockage_frigo: rapport.enc_stockage_frigo || '',
      enc_location: rapport.enc_location || '',
      enc_tickets: rapport.enc_tickets || '',
      enc_autre: rapport.enc_autre || '',
      enc_autre_libelle: rapport.enc_autre_libelle || '',
      dec_montant: rapport.dec_montant || '',
      dec_libelle: rapport.dec_libelle || '',
      observations: rapport.observations || '',
    });
    setOnglet('correction');
  };

  const sauvegarderCorrection = async () => {
    if (!rapportACorrection) return;
    setChargement(true); setMessage(null);
    try {
      await api.put('/caisses/' + rapportACorrection.id + '/corriger', form);
      setMessage({ type:'ok', texte:'✅ Rapport corrigé et remis en brouillon.' });
      setTimeout(() => { setOnglet('liste'); setRapportACorrection(null); setMessage(null); }, 1500);
    } catch(err) { setMessage({ type:'err', texte: err.response?.data?.message || 'Erreur serveur.' }); }
    finally { setChargement(false); }
  };

  const COUPURES = [
    { key:'nb_10000', val:10000, label:'10 000 F' },
    { key:'nb_5000',  val:5000,  label:'5 000 F'  },
    { key:'nb_2000',  val:2000,  label:'2 000 F'  },
    { key:'nb_1000',  val:1000,  label:'1 000 F'  },
    { key:'nb_500',   val:500,   label:'500 F'    },
    { key:'nb_200',   val:200,   label:'200 F'    },
    { key:'nb_100',   val:100,   label:'100 F'    },
    { key:'nb_50',    val:50,    label:'50 F'     },
    { key:'nb_25',    val:25,    label:'25 F'     },
    { key:'nb_10',    val:10,    label:'10 F'     },
    { key:'nb_5',     val:5,     label:'5 F'      },
  ];

  const totalBilletage = () => COUPURES.reduce((s,c) => s + (parseInt(billetage[c.key])||0) * c.val, 0);

  const ouvrirBilletage = async (rapport) => {
    setRapportBilletage(rapport);
    setBilletage({ nb_10000:0,nb_5000:0,nb_2000:0,nb_1000:0,nb_500:0,nb_200:0,nb_100:0,nb_50:0,nb_25:0,nb_10:0,nb_5:0 });
    setBilletageExistant(null);
    try {
      const r = await api.get('/billetage/' + rapport.id);
      if (r.data) {
        setBilletageExistant(r.data);
        setBilletage({ nb_10000:r.data.nb_10000,nb_5000:r.data.nb_5000,nb_2000:r.data.nb_2000,nb_1000:r.data.nb_1000,nb_500:r.data.nb_500,nb_200:r.data.nb_200,nb_100:r.data.nb_100,nb_50:r.data.nb_50,nb_25:r.data.nb_25,nb_10:r.data.nb_10,nb_5:r.data.nb_5 });
      }
    } catch {}
    setOnglet('billetage');
  };

  const sauvegarderBilletage = async () => {
    if (!rapportBilletage) return;
    setChargement(true); setMessage(null);
    try {
      await api.post('/billetage/' + rapportBilletage.id, billetage);
      setMessage({ type:'ok', texte:'✅ Billetage sauvegardé.' });
      setTimeout(() => { setOnglet('liste'); setRapportBilletage(null); setMessage(null); }, 1500);
    } catch(err) { setMessage({ type:'err', texte: err.response?.data?.message || 'Erreur.' }); }
    finally { setChargement(false); }
  };

  const valider = async (rapport) => {
    try {
      await api.put('/caisses/' + rapport.id + '/valider', {});
      setMessage({ type:'ok', texte:'✅ Rapport validé.' });
      charger(page);
    } catch(err) { setMessage({ type:'err', texte: err.response?.data?.message || 'Erreur.' }); }
  };

  const rejeter = async () => {
    if (!rapportARejeter) return;
    try {
      await api.put('/caisses/' + rapportARejeter.id + '/rejeter', { motif_rejet: motifRejet });
      setMessage({ type:'ok', texte:'🔴 Rapport rejeté.' });
      setRapportARejeter(null); setMotifRejet('');
      charger(page);
    } catch(err) { setMessage({ type:'err', texte: err.response?.data?.message || 'Erreur.' }); }
  };

  const sauvegarder = async (soumettre=false) => {
    if (!form.abattoir_id || !form.date_rapport) return setMessage({ type:'err', texte:'Abattoir et date obligatoires.' });
    setChargement(true); setMessage(null);
    try {
      const r = await api.post('/caisses', form);
      if (soumettre) await api.put(`/caisses/${r.data.id}/soumettre`);
      setMessage({ type:'ok', texte: soumettre ? '✅ Rapport soumis avec succès !' : '💾 Brouillon sauvegardé.' });
      setTimeout(() => { setOnglet('liste'); setMessage(null); }, 1500);
    } catch(err) { setMessage({ type:'err', texte: err.response?.data?.message || 'Erreur serveur.' }); }
    finally { setChargement(false); }
  };

  return (
    <div style={S.page}>
      {/* Topbar */}
      <div style={S.topbar}>
        <div style={S.breadcrumb}>
          <span>Caisse</span>
          <span style={{ color: C.border }}>›</span>
          <span style={S.breadcrumbActive}>Gestion des caisses</span>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          {onglet === 'liste' && (
            <button onClick={() => setOnglet('saisie')} style={S.btnPrimary}>
              + Nouveau rapport
            </button>
          )}
          {onglet === 'saisie' && (
            <button onClick={() => setOnglet('liste')} style={S.btnSecondaire}>✖ Annuler</button>
          )}
          {onglet === 'correction' && (
            <button onClick={() => { setOnglet('liste'); setRapportACorrection(null); }} style={S.btnSecondaire}>✖ Annuler correction</button>
          )}
          {onglet === 'billetage' && (
            <button onClick={() => { setOnglet('liste'); setRapportBilletage(null); }} style={S.btnSecondaire}>✖ Annuler billetage</button>
          )}
        </div>
      </div>

      <div style={S.container}>

        {/* ── KPI STATS ── */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(200px,1fr))', gap:14, marginBottom:20 }}>
          {[
            { label:'Total recettes', value: fmt(stats.total_recettes), icon:'💵', color: C.primary, bg: C.green100 },
            { label:'Rapports de caisse', value: stats.nb_rapports||0, icon:'📋', color:'#1565C0', bg:'#E3F2FD' },
            { label:'Caisses actives', value: stats.nb_factures||0, icon:'🏪', color:'#E65100', bg:'#FFF3E0' },
          ].map((c,i) => (
            <div key={i} style={{ background:'#fff', borderRadius:10, padding:'16px 18px', boxShadow:'0 1px 3px rgba(0,0,0,.06)', border:`0.5px solid ${C.border}`, display:'flex', alignItems:'center', gap:14 }}>
              <div style={{ width:42, height:42, background:c.bg, borderRadius:10, display:'flex', alignItems:'center', justifyContent:'center', fontSize:20 }}>{c.icon}</div>
              <div>
                <div style={{ fontSize:11, color: C.textLight, fontWeight:600, textTransform:'uppercase', letterSpacing:.5 }}>{c.label}</div>
                <div style={{ fontSize:22, fontWeight:700, color:c.color, marginTop:2 }}>{c.value}</div>
              </div>
            </div>
          ))}
        </div>

        {/* ── MESSAGE ── */}
        {message && <div style={S.alerte(message.type)}>{message.texte}</div>}

        {/* ══ LISTE ══ */}
        {onglet === 'liste' && (
          <div style={S.carte}>
            {/* Filtres */}
            <div style={{ padding:'12px 16px', borderBottom:`1px solid ${C.border}`, display:'flex', gap:12, flexWrap:'wrap', alignItems:'flex-end', background:'#FAFAFA' }}>
              <div>
                <label style={S.label}>Date</label>
                <input type="date" value={filtreDate} onChange={e=>setFiltreDate(e.target.value)}
                  style={{ padding:'6px 10px', border:`1px solid ${C.border}`, borderRadius:6, fontSize:12, outline:'none' }}/>
              </div>
              {isAdmin && (
                <div>
                  <label style={S.label}>Abattoir</label>
                  <select value={filtreAbattoir} onChange={e=>setFiltreAbattoir(e.target.value)}
                    style={{ padding:'6px 10px', border:`1px solid ${C.border}`, borderRadius:6, fontSize:12, outline:'none', minWidth:150 }}>
                    <option value="">Tous les abattoirs</option>
                    {abattoirs.map(a=><option key={a.id} value={a.id}>{a.nom}</option>)}
                  </select>
                </div>
              )}
              <div>
                <label style={S.label}>Statut</label>
                <select value={filtreStatut} onChange={e=>setFiltreStatut(e.target.value)}
                  style={{ padding:'6px 10px', border:`1px solid ${C.border}`, borderRadius:6, fontSize:12, outline:'none', minWidth:130 }}>
                  <option value="">Tous les statuts</option>
                  {Object.entries(STATUT_CONFIG).map(([k,v])=><option key={k} value={k}>{v.label}</option>)}
                </select>
              </div>
              <button onClick={() => charger(1)} style={{ ...S.btnPrimary, padding:'6px 14px' }}>Filtrer</button>
              <button onClick={() => { setFiltreDate(''); setFiltreAbattoir(''); setFiltreStatut(''); }}
                style={{ padding:'6px 12px', background:'#fff', color: C.textMid, border:`1px solid ${C.border}`, borderRadius:6, fontSize:12, cursor:'pointer' }}>Effacer</button>
              <div style={{ flex:1 }}/>
              <span style={{ fontSize:12, color: C.textLight, alignSelf:'center' }}>{total} résultat{total>1?'s':''}</span>
            </div>

            {chargement ? (
              <div style={{ padding:48, textAlign:'center', color: C.textLight }}>Chargement...</div>
            ) : (
              <table style={{ width:'100%', borderCollapse:'collapse' }}>
                <thead>
                  <tr>
                    {['Date','Abattoir','Caissier','Encaissé','Décaissé','Solde net','Statut','Billetage','Actions'].map(h => (
                      <th key={h} style={{ ...S.th, textAlign:['Encaissé','Décaissé','Solde net'].includes(h)?'right':'left' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rapports.length === 0 ? (
                    <tr><td colSpan={9} style={{ padding:48, textAlign:'center', color: C.textLight, fontSize:13 }}>
                      <div style={{ fontSize:32, marginBottom:8 }}>📭</div>Aucun rapport trouvé
                    </td></tr>
                  ) : rapports.map((r,i) => (
                    <tr key={r.id} style={{ background: i%2===0?'#fff':'#FAFAFA' }}>
                      <td style={{ ...S.td, fontWeight:500 }}>{fmtDate(r.date_rapport)}</td>
                      <td style={S.td}>
                        <span style={{ display:'inline-flex', alignItems:'center', gap:6 }}>
                          <span style={{ width:7, height:7, borderRadius:'50%', background: C.primary, display:'inline-block' }}/>
                          {r.abattoir_nom||'—'}
                        </span>
                      </td>
                      <td style={S.td}>{r.caissier_nom||'—'}</td>
                      <td style={{ ...S.tdR, color: C.primary, fontWeight:600 }}>{fmt(r.total_encaisse)}</td>
                      <td style={{ ...S.tdR, color:'#C62828' }}>{fmt(r.total_decaisse)}</td>
                      <td style={{ ...S.tdR, fontWeight:700, color: r.solde_net<0?'#C62828': C.textDark }}>{fmt(r.solde_net)}</td>
                      <td style={S.td}><BadgeStatut statut={r.statut}/></td>
                      <td style={S.td}>
                        {r.billetage_statut === 'absent' && (
                          <span style={{ fontSize:11, color: C.textLight, fontStyle:'italic' }}>Non fait</span>
                        )}
                        {r.billetage_statut === 'ok' && (
                          <span style={{ display:'inline-flex', alignItems:'center', gap:4, background: C.green100, color: C.primary, padding:'3px 8px', borderRadius:20, fontSize:11, fontWeight:700 }}>
                            ✅ Équilibré
                          </span>
                        )}
                        {r.billetage_statut === 'arrondi' && (
                          <span style={{ display:'inline-flex', alignItems:'center', gap:4, background:'#FFF8E1', color:'#E65100', padding:'3px 8px', borderRadius:20, fontSize:11, fontWeight:700 }} title={'Arrondi : ' + (r.billetage_ecart > 0 ? '+' : '') + Number(r.billetage_ecart).toLocaleString('fr-FR') + ' F'}>
                            🔄 Arrondi {r.billetage_ecart > 0 ? '+' : ''}{Number(r.billetage_ecart).toLocaleString('fr-FR')} F
                          </span>
                        )}
                        {r.billetage_statut === 'ecart' && (
                          <span style={{ display:'inline-flex', alignItems:'center', gap:4, background:'#FFEBEE', color:'#C62828', padding:'3px 8px', borderRadius:20, fontSize:11, fontWeight:700 }} title={'Écart : ' + (r.billetage_ecart > 0 ? '+' : '') + Number(r.billetage_ecart).toLocaleString('fr-FR') + ' F'}>
                            ⚠️ Écart {r.billetage_ecart > 0 ? '+' : ''}{Number(r.billetage_ecart).toLocaleString('fr-FR')} F
                          </span>
                        )}
                      </td>
                      <td style={S.td}>
                        {r.statut === 'BROUILLON' && (isAdmin || r.caissier_id === utilisateur?.id) && (
                          <div style={{ display:'flex', gap:5, flexWrap:'wrap', marginBottom:4 }}>
                            <button onClick={() => ouvririCorrection(r)} style={{ padding:'4px 10px', background:'#E3F2FD', border:'1px solid #90CAF9', color:'#1565C0', borderRadius:5, fontSize:11, cursor:'pointer', fontWeight:600 }}>
                              ✏️ Modifier
                            </button>
                            <button onClick={async () => {
                              try {
                                await api.put('/caisses/' + r.id + '/soumettre');
                                setMessage({ type:'ok', texte:'📤 Rapport soumis pour validation.' });
                                charger(page);
                              } catch(err) { setMessage({ type:'err', texte: err.response?.data?.message || 'Erreur.' }); }
                            }} style={{ padding:'4px 10px', background: C.green100, border:'1px solid #A5D6A7', color: C.primaryDark, borderRadius:5, fontSize:11, cursor:'pointer', fontWeight:600 }}>
                              📤 Soumettre
                            </button>
                          </div>
                        )}
                        {r.statut === 'REJETE' && (isAdmin || isCaissierPrincipal || r.caissier_id === utilisateur?.id) && (
                          <button onClick={() => ouvririCorrection(r)} style={{ padding:'4px 10px', background:'#FFF3E0', border:'1px solid #FFCC80', color:'#E65100', borderRadius:5, fontSize:11, cursor:'pointer', fontWeight:600 }}>
                            ✏️ Corriger
                          </button>
                        )}
                        {r.statut === 'VALIDE' && isAdmin && (
                          <button onClick={() => ouvririCorrection(r)} style={{ padding:'4px 10px', background:'#E3F2FD', border:'1px solid #90CAF9', color:'#1565C0', borderRadius:5, fontSize:11, cursor:'pointer', fontWeight:600 }}>
                            ✏️ Modifier
                          </button>
                        )}
                        {r.statut === 'REJETE' && r.motif_rejet && (
                          <div style={{ fontSize:11, color:'#C62828', marginTop:4, maxWidth:140 }} title={r.motif_rejet}>
                            ⚠ {r.motif_rejet.substring(0,40)}{r.motif_rejet.length>40?'...':''}
                          </div>
                        )}
                        {r.statut === 'SOUMIS' && isAdmin && (
                          <div style={{ display:'flex', gap:5, flexWrap:'wrap' }}>
                            <button onClick={() => valider(r)} style={{ padding:'4px 10px', background: C.green100, border:'1px solid #A5D6A7', color: C.primaryDark, borderRadius:5, fontSize:11, cursor:'pointer', fontWeight:600 }}>
                              ✅ Valider
                            </button>
                            <button onClick={() => { setRapportARejeter(r); setMotifRejet(''); }} style={{ padding:'4px 10px', background:'#FFEBEE', border:'1px solid #EF9A9A', color:'#C62828', borderRadius:5, fontSize:11, cursor:'pointer', fontWeight:600 }}>
                              ❌ Rejeter
                            </button>
                          </div>
                        )}
                        {(r.statut === 'SOUMIS' || r.statut === 'VALIDE' || r.statut === 'BROUILLON') && (isAdmin || isCaissierPrincipal || r.caissier_id === utilisateur?.id) && (
                          <button onClick={() => ouvrirBilletage(r)} style={{ padding:'4px 10px', background:'#F3E5F5', border:'1px solid #CE93D8', color:'#6A1B9A', borderRadius:5, fontSize:11, cursor:'pointer', fontWeight:600, marginTop:4 }}>
                            💵 Billetage
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {/* Pagination */}
            {nbPages > 1 && (
              <div style={{ padding:'12px 16px', borderTop:`1px solid ${C.border}`, display:'flex', alignItems:'center', justifyContent:'space-between', background:'#FAFAFA', borderRadius:'0 0 12px 12px' }}>
                <span style={{ fontSize:12, color: C.textMid }}>Page <strong>{page}</strong> sur <strong>{nbPages}</strong> — {total} rapports</span>
                <div style={{ display:'flex', gap:4 }}>
                  <button onClick={()=>charger(1)} disabled={page===1} style={{ padding:'5px 10px', border:`1px solid ${C.border}`, borderRadius:6, background:'#fff', color:page===1? C.textLight: C.textDark, cursor:page===1?'default':'pointer', fontSize:12 }}>«</button>
                  <button onClick={()=>charger(page-1)} disabled={page===1} style={{ padding:'5px 12px', border:`1px solid ${C.border}`, borderRadius:6, background:'#fff', color:page===1? C.textLight: C.textDark, cursor:page===1?'default':'pointer', fontSize:12 }}>‹</button>
                  {Array.from({length: Math.min(5, nbPages)}, (_,i) => {
                    const p = page <= 3 ? i+1 : page-2+i;
                    if (p > nbPages) return null;
                    return <button key={p} onClick={()=>charger(p)} style={{ padding:'5px 11px', border:`1px solid ${p===page? C.primary: C.border}`, borderRadius:6, background:p===page? C.primary:'#fff', color:p===page?'#fff': C.textDark, cursor:'pointer', fontSize:12, fontWeight:p===page?700:400 }}>{p}</button>;
                  })}
                  <button onClick={()=>charger(page+1)} disabled={page===nbPages} style={{ padding:'5px 12px', border:`1px solid ${C.border}`, borderRadius:6, background:'#fff', color:page===nbPages? C.textLight: C.textDark, cursor:page===nbPages?'default':'pointer', fontSize:12 }}>›</button>
                  <button onClick={()=>charger(nbPages)} disabled={page===nbPages} style={{ padding:'5px 10px', border:`1px solid ${C.border}`, borderRadius:6, background:'#fff', color:page===nbPages? C.textLight: C.textDark, cursor:page===nbPages?'default':'pointer', fontSize:12 }}>»</button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ══ SAISIE ══ */}
        {onglet === 'saisie' && (
          <div style={{ maxWidth:860, margin:'0 auto' }}>
            <div style={S.carte}>
              <div style={S.secHeader()}>📄 Informations générales</div>
              <div style={{ ...S.carteBody, display:'flex', gap:16, flexWrap:'wrap' }}>
                <div style={{ display:'flex', flexDirection:'column', gap:5, flex:1, minWidth:200 }}>
                  <label style={S.label}>Abattoir <span style={{color:'#C62828'}}>*</span></label>
                  <select value={form.abattoir_id} onChange={e=>champ('abattoir_id',e.target.value)} disabled={!isAdmin}
                    style={{ padding:'8px 10px', border:`1px solid ${C.border}`, borderRadius:7, fontSize:12, outline:'none' }}>
                    <option value="">-- Sélectionner --</option>
                    {abattoirs.map(a=><option key={a.id} value={a.id}>{a.nom}</option>)}
                  </select>
                </div>
                <div style={{ display:'flex', flexDirection:'column', gap:5, flex:1, minWidth:160 }}>
                  <label style={S.label}>Date du rapport <span style={{color:'#C62828'}}>*</span></label>
                  <input type="date" value={form.date_rapport} onChange={e=>champ('date_rapport',e.target.value)}
                    style={{ padding:'8px 10px', border:`1px solid ${C.border}`, borderRadius:7, fontSize:12, outline:'none' }}/>
                </div>
              </div>
            </div>

            <div style={S.carte}>
              <div style={S.secHeader()}>💵 Encaissements</div>
              <div style={S.carteBody}>
                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(150px,1fr))', gap:12, marginBottom:12 }}>
                  {[['enc_abattoirs',"Droits d'abattage"],['enc_stockage_frigo','Stockage frigo'],['enc_location','Location'],['enc_tickets','Tickets'],['enc_autre','Autres']].map(([k,l])=>(
                    <div key={k}>
                      <label style={S.label}>{l}</label>
                      <div style={{ position:'relative' }}>
                        <input type="number" min="0" value={form[k]} onChange={e=>champ(k,e.target.value)} placeholder="0"
                          style={{ width:'100%', padding:'8px 32px 8px 10px', border:`1px solid ${C.border}`, borderRadius:7, fontSize:12, outline:'none', boxSizing:'border-box' }}/>
                        <span style={{ position:'absolute', right:8, top:'50%', transform:'translateY(-50%)', fontSize:11, color: C.textLight }}>F</span>
                      </div>
                    </div>
                  ))}
                </div>
                {form.enc_autre && (
                  <div style={{ marginBottom:12 }}>
                    <label style={S.label}>Préciser les autres recettes</label>
                    <input value={form.enc_autre_libelle} onChange={e=>champ('enc_autre_libelle',e.target.value)}
                      style={{ width:'100%', padding:'8px 10px', border:`1px solid ${C.border}`, borderRadius:7, fontSize:12, outline:'none', boxSizing:'border-box' }}/>
                  </div>
                )}
                <div style={{ background: C.green100, border:'1px solid #A5D6A7', borderRadius:8, padding:'10px 14px', display:'flex', justifyContent:'space-between' }}>
                  <span style={{ fontSize:13, color: C.primaryDark, fontWeight:500 }}>Total encaissé</span>
                  <span style={{ fontSize:20, fontWeight:700, color: C.primary }}>{fmt(totalEnc())}</span>
                </div>
              </div>
            </div>

            <div style={S.carte}>
              <div style={S.secHeader('#B71C1C')}>💸 Décaissements</div>
              <div style={S.carteBody}>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 2fr', gap:12, marginBottom:12 }}>
                  <div>
                    <label style={S.label}>Montant décaissé</label>
                    <div style={{ position:'relative' }}>
                      <input type="number" min="0" value={form.dec_montant} onChange={e=>champ('dec_montant',e.target.value)} placeholder="0"
                        style={{ width:'100%', padding:'8px 32px 8px 10px', border:`1px solid ${C.border}`, borderRadius:7, fontSize:12, outline:'none', boxSizing:'border-box' }}/>
                      <span style={{ position:'absolute', right:8, top:'50%', transform:'translateY(-50%)', fontSize:11, color: C.textLight }}>F</span>
                    </div>
                  </div>
                  <div>
                    <label style={S.label}>Motif</label>
                    <input value={form.dec_libelle} onChange={e=>champ('dec_libelle',e.target.value)}
                      style={{ padding:'8px 10px', border:`1px solid ${C.border}`, borderRadius:7, fontSize:12, outline:'none', width:'100%', boxSizing:'border-box' }}/>
                  </div>
                </div>
                <div style={{ background:soldeNet()<0?'#FFEBEE': C.green100, border:`1px solid ${soldeNet()<0?'#EF9A9A':'#A5D6A7'}`, borderRadius:8, padding:'10px 14px', display:'flex', justifyContent:'space-between' }}>
                  <span style={{ fontSize:13, fontWeight:500, color:soldeNet()<0?'#B71C1C': C.primaryDark }}>Solde net</span>
                  <span style={{ fontSize:20, fontWeight:700, color:soldeNet()<0?'#C62828': C.primary }}>{fmt(soldeNet())}</span>
                </div>
              </div>
            </div>

            <div style={S.carte}>
              <div style={S.secHeader()}>📝 Observations</div>
              <div style={S.carteBody}>
                <textarea value={form.observations} onChange={e=>champ('observations',e.target.value)} placeholder="Observations, incidents, remarques..."
                  style={{ width:'100%', padding:'10px 12px', border:`1px solid ${C.border}`, borderRadius:7, fontSize:12, outline:'none', minHeight:80, resize:'vertical', boxSizing:'border-box' }}/>
              </div>
            </div>

            <div style={{ display:'flex', gap:12, justifyContent:'flex-end' }}>
              <button onClick={()=>setOnglet('liste')} style={S.btnSecondaire}>Annuler</button>
              <button onClick={()=>sauvegarder(false)} disabled={chargement} style={{ padding:'10px 20px', background:'#F4F6F9', border:`1px solid ${C.border}`, borderRadius:7, fontSize:13, color: C.textDark, cursor:'pointer', fontWeight:600 }}>💾 Brouillon</button>
              <button onClick={()=>sauvegarder(true)} disabled={chargement} style={{ padding:'10px 24px', background: C.primary, border:'none', borderRadius:7, fontSize:13, color:'#fff', cursor:'pointer', fontWeight:700 }}>📤 Soumettre</button>
            </div>
          </div>
        )}

        {/* ══ CORRECTION ══ */}
        {onglet === 'correction' && rapportACorrection && (
          <div style={{ maxWidth:860, margin:'0 auto' }}>
            <div style={{ background:'#FFF3E0', border:'1px solid #FFCC80', borderRadius:10, padding:'14px 18px', marginBottom:16 }}>
              <div style={{ fontWeight:700, color:'#E65100', marginBottom:4 }}>⚠️ Correction du rapport rejeté</div>
              <div style={{ fontSize:13, color:'#BF360C' }}>Motif du rejet : <strong>{rapportACorrection.motif_rejet || '—'}</strong></div>
              <div style={{ fontSize:12, color:'#BF360C', marginTop:4 }}>Abattoir : {rapportACorrection.abattoir_nom} — Date : {fmtDate(rapportACorrection.date_rapport)}</div>
            </div>

            <div style={S.carte}>
              <div style={S.secHeader()}>💵 Encaissements</div>
              <div style={S.carteBody}>
                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(150px,1fr))', gap:12, marginBottom:12 }}>
                  {[['enc_abattoirs',"Droits d'abattage"],['enc_stockage_frigo','Stockage frigo'],['enc_location','Location'],['enc_tickets','Tickets'],['enc_autre','Autres']].map(([k,l])=>(
                    <div key={k}>
                      <label style={S.label}>{l}</label>
                      <div style={{ position:'relative' }}>
                        <input type="number" min="0" value={form[k]} onChange={e=>champ(k,e.target.value)} placeholder="0"
                          style={{ width:'100%', padding:'8px 32px 8px 10px', border:`1px solid ${C.border}`, borderRadius:7, fontSize:12, outline:'none', boxSizing:'border-box' }}/>
                        <span style={{ position:'absolute', right:8, top:'50%', transform:'translateY(-50%)', fontSize:11, color: C.textLight }}>F</span>
                      </div>
                    </div>
                  ))}
                </div>
                <div style={{ background: C.green100, border:'1px solid #A5D6A7', borderRadius:8, padding:'10px 14px', display:'flex', justifyContent:'space-between' }}>
                  <span style={{ fontSize:13, color: C.primaryDark, fontWeight:500 }}>Total encaissé</span>
                  <span style={{ fontSize:20, fontWeight:700, color: C.primary }}>{fmt(totalEnc())}</span>
                </div>
              </div>
            </div>

            <div style={S.carte}>
              <div style={S.secHeader('#B71C1C')}>💸 Décaissements</div>
              <div style={S.carteBody}>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 2fr', gap:12, marginBottom:12 }}>
                  <div>
                    <label style={S.label}>Montant décaissé</label>
                    <div style={{ position:'relative' }}>
                      <input type="number" min="0" value={form.dec_montant} onChange={e=>champ('dec_montant',e.target.value)} placeholder="0"
                        style={{ width:'100%', padding:'8px 32px 8px 10px', border:`1px solid ${C.border}`, borderRadius:7, fontSize:12, outline:'none', boxSizing:'border-box' }}/>
                      <span style={{ position:'absolute', right:8, top:'50%', transform:'translateY(-50%)', fontSize:11, color: C.textLight }}>F</span>
                    </div>
                  </div>
                  <div>
                    <label style={S.label}>Motif</label>
                    <input value={form.dec_libelle} onChange={e=>champ('dec_libelle',e.target.value)}
                      style={{ padding:'8px 10px', border:`1px solid ${C.border}`, borderRadius:7, fontSize:12, outline:'none', width:'100%', boxSizing:'border-box' }}/>
                  </div>
                </div>
                <div style={{ background:soldeNet()<0?'#FFEBEE': C.green100, border:`1px solid ${soldeNet()<0?'#EF9A9A':'#A5D6A7'}`, borderRadius:8, padding:'10px 14px', display:'flex', justifyContent:'space-between' }}>
                  <span style={{ fontSize:13, fontWeight:500, color:soldeNet()<0?'#B71C1C': C.primaryDark }}>Solde net</span>
                  <span style={{ fontSize:20, fontWeight:700, color:soldeNet()<0?'#C62828': C.primary }}>{fmt(soldeNet())}</span>
                </div>
              </div>
            </div>

            <div style={S.carte}>
              <div style={S.secHeader()}>📝 Observations</div>
              <div style={S.carteBody}>
                <textarea value={form.observations} onChange={e=>champ('observations',e.target.value)}
                  style={{ width:'100%', padding:'10px 12px', border:`1px solid ${C.border}`, borderRadius:7, fontSize:12, outline:'none', minHeight:80, resize:'vertical', boxSizing:'border-box' }}/>
              </div>
            </div>

            <div style={{ display:'flex', gap:12, justifyContent:'flex-end' }}>
              <button onClick={() => { setOnglet('liste'); setRapportACorrection(null); }} style={S.btnSecondaire}>Annuler</button>
              <button onClick={sauvegarderCorrection} disabled={chargement} style={{ padding:'10px 24px', background:'#E65100', border:'none', borderRadius:7, fontSize:13, color:'#fff', cursor:'pointer', fontWeight:700 }}>✅ Valider la correction</button>
            </div>
          </div>
        )}

        {/* ══ BILLETAGE ══ */}
        {onglet === 'billetage' && rapportBilletage && (
          <div style={{ maxWidth:700, margin:'0 auto' }}>
            <div style={{ background:'#F3E5F5', border:'1px solid #CE93D8', borderRadius:10, padding:'14px 18px', marginBottom:16 }}>
              <div style={{ fontWeight:700, color:'#6A1B9A', marginBottom:4 }}>💵 Billetage du rapport</div>
              <div style={{ fontSize:13, color:'#4A148C' }}>{rapportBilletage.abattoir_nom} — {fmtDate(rapportBilletage.date_rapport)} — Caissier : {rapportBilletage.caissier_nom}</div>
              <div style={{ fontSize:13, color:'#4A148C', marginTop:4 }}>Total encaissé : <strong>{fmt(rapportBilletage.total_encaisse)}</strong></div>
              {billetageExistant && <div style={{ fontSize:12, color:'#6A1B9A', marginTop:4 }}>✏️ Billetage existant — dernière mise à jour</div>}
            </div>

            <div style={S.carte}>
              <div style={S.secHeader('#6A1B9A')}>Détail des coupures</div>
              <div style={S.carteBody}>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:0 }}>
                  {[
                    { key:'nb_10000', val:10000, label:'Billets 10 000 F', color: C.primaryDark },
                    { key:'nb_5000',  val:5000,  label:'Billets 5 000 F',  color: C.primaryDark },
                    { key:'nb_2000',  val:2000,  label:'Billets 2 000 F',  color:'#1565C0' },
                    { key:'nb_1000',  val:1000,  label:'Billets 1 000 F',  color:'#1565C0' },
                    { key:'nb_500',   val:500,   label:'Billets 500 F',    color:'#E65100' },
                    { key:'nb_200',   val:200,   label:'Billets 200 F',    color:'#E65100' },
                    { key:'nb_100',   val:100,   label:'Pièces 100 F',     color: C.textDark },
                    { key:'nb_50',    val:50,    label:'Pièces 50 F',      color: C.textDark },
                    { key:'nb_25',    val:25,    label:'Pièces 25 F',      color: C.textDark },
                    { key:'nb_10',    val:10,    label:'Pièces 10 F',      color: C.textMid },
                    { key:'nb_5',     val:5,     label:'Pièces 5 F',       color: C.textMid },
                  ].map((c,i) => (
                    <div key={c.key} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'9px 14px', borderBottom:`1px solid #F0F2F4`, background: i%2===0?'#fff':'#FAFAFA' }}>
                      <div style={{ display:'flex', alignItems:'center', gap:10, flex:1 }}>
                        <span style={{ fontSize:12, fontWeight:600, color:c.color, minWidth:120 }}>{c.label}</span>
                        <input type="number" min="0" value={billetage[c.key]} onChange={e => setBilletage(b => ({...b, [c.key]: parseInt(e.target.value)||0}))}
                          style={{ width:65, padding:'5px 7px', border:`1px solid ${C.border}`, borderRadius:6, fontSize:12, outline:'none', textAlign:'center' }}/>
                        <span style={{ fontSize:11, color: C.textLight }}>× {c.val.toLocaleString('fr-FR')} F</span>
                      </div>
                      <span style={{ fontSize:12, fontWeight:600, color:c.color, minWidth:90, textAlign:'right' }}>
                        {fmt((parseInt(billetage[c.key])||0) * c.val)}
                      </span>
                    </div>
                  ))}
                </div>

                <div style={{ marginTop:14, padding:'14px 16px', background:'#F8F9FA', borderRadius:8, border:`1px solid ${C.border}` }}>
                  <div style={{ display:'flex', justifyContent:'space-between', marginBottom:8 }}>
                    <span style={{ fontSize:13, color: C.textDark, fontWeight:500 }}>Total billetage</span>
                    <span style={{ fontSize:18, fontWeight:700, color:'#6A1B9A' }}>{fmt(totalBilletage())}</span>
                  </div>
                  <div style={{ display:'flex', justifyContent:'space-between', marginBottom:8 }}>
                    <span style={{ fontSize:13, color: C.textDark, fontWeight:500 }}>Total encaissé</span>
                    <span style={{ fontSize:18, fontWeight:700, color: C.primary }}>{fmt(rapportBilletage.total_encaisse)}</span>
                  </div>
                  <div style={{ borderTop:`1px solid ${C.border}`, paddingTop:8, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                    <span style={{ fontSize:14, fontWeight:700, color: C.textDark }}>Écart</span>
                    <span style={{ fontSize:20, fontWeight:800, color: Math.abs(totalBilletage() - rapportBilletage.total_encaisse) < 1 ? C.primary : '#C62828' }}>
                      {totalBilletage() - rapportBilletage.total_encaisse >= 0 ? '+' : ''}{fmt(totalBilletage() - rapportBilletage.total_encaisse)}
                    </span>
                  </div>
                  {(() => {
                    const ecart = totalBilletage() - parseFloat(rapportBilletage.total_encaisse || 0);
                    const absEcart = Math.abs(ecart);
                    if (totalBilletage() === 0) return null;
                    if (absEcart === 0) return (
                      <div style={{ marginTop:10, padding:'10px 14px', background: C.green100, border:'1px solid #A5D6A7', borderRadius:8, textAlign:'center', color: C.primaryDark, fontSize:13, fontWeight:700 }}>
                        ✅ Billetage parfaitement équilibré
                      </div>
                    );
                    if (absEcart < 5) return (
                      <div style={{ marginTop:10, padding:'10px 14px', background:'#FFF8E1', border:'1px solid #FFCC80', borderRadius:8 }}>
                        <div style={{ textAlign:'center', color:'#E65100', fontSize:13, fontWeight:700 }}>
                          🔄 Arrondi de {ecart > 0 ? '+' : ''}{fmt(ecart)}
                        </div>
                        <div style={{ textAlign:'center', color:'#BF360C', fontSize:12, marginTop:4 }}>
                          Écart inférieur à 5 F — considéré comme arrondi acceptable
                        </div>
                      </div>
                    );
                    return (
                      <div style={{ marginTop:10, padding:'10px 14px', background:'#FFEBEE', border:'1px solid #EF9A9A', borderRadius:8 }}>
                        <div style={{ textAlign:'center', color:'#C62828', fontSize:14, fontWeight:700 }}>
                          ⚠️ Écart de {ecart > 0 ? '+' : ''}{fmt(ecart)}
                        </div>
                        <div style={{ textAlign:'center', color:'#B71C1C', fontSize:12, marginTop:4 }}>
                          {ecart > 0
                            ? 'Le billetage est supérieur au montant encaissé — vérifiez les coupures'
                            : 'Le billetage est inférieur au montant encaissé — il manque des billets/pièces'}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>
            </div>

            <div style={{ display:'flex', gap:12, justifyContent:'flex-end' }}>
              <button onClick={() => { setOnglet('liste'); setRapportBilletage(null); }} style={S.btnSecondaire}>Annuler</button>
              <button onClick={sauvegarderBilletage} disabled={chargement} style={{ padding:'10px 24px', background:'#6A1B9A', border:'none', borderRadius:7, fontSize:13, color:'#fff', cursor:'pointer', fontWeight:700 }}>
                💾 Sauvegarder le billetage
              </button>
            </div>
          </div>
        )}

        {/* ══ MODAL REJET ══ */}
        {rapportARejeter && (
          <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center' }}>
            <div style={{ background:'#fff', borderRadius:12, padding:28, width:440, boxShadow:'0 20px 60px rgba(0,0,0,.2)' }}>
              <h3 style={{ margin:'0 0 8px', fontSize:16, fontWeight:700, color: C.textDark }}>❌ Rejeter le rapport</h3>
              <p style={{ fontSize:13, color: C.textMid, margin:'0 0 16px' }}>
                {rapportARejeter.abattoir_nom} — {fmtDate(rapportARejeter.date_rapport)}
              </p>
              <label style={{ ...S.label, marginBottom:6 }}>Motif du rejet <span style={{color:'#C62828'}}>*</span></label>
              <textarea value={motifRejet} onChange={e=>setMotifRejet(e.target.value)} placeholder="Expliquer la raison du rejet..."
                style={{ width:'100%', padding:'10px 12px', border:`1px solid ${C.border}`, borderRadius:7, fontSize:12, outline:'none', minHeight:90, resize:'vertical', boxSizing:'border-box', marginBottom:20 }}/>
              <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
                <button onClick={() => setRapportARejeter(null)} style={S.btnSecondaire}>Annuler</button>
                <button onClick={rejeter} disabled={!motifRejet.trim()} style={{ padding:'9px 20px', background: motifRejet.trim()?'#C62828':'#EF9A9A', border:'none', borderRadius:7, fontSize:13, color:'#fff', cursor: motifRejet.trim()?'pointer':'default', fontWeight:700 }}>Confirmer le rejet</button>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
